import type { FreshchatUserConfig } from '../types/freshchat';
import { getLogger } from '@/lib/logger';

export class SafeFreshchatWrapper {
  private reCheckTimer: number;
  private isLoadedFlag: boolean = false;
  private isInitializedFlag: boolean = false;

  constructor(reCheckTimer: number = 1000) {
    this.reCheckTimer = reCheckTimer;
  }

  private checkFreshchatLoaded(): Promise<boolean> {
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (window.fcWidget && window.fcWidget.isInitialized()) {
          this.isLoadedFlag = true;
          this.isInitializedFlag = true;
          clearInterval(checkInterval);
          resolve(true);
        }
      }, this.reCheckTimer);
    });
  }

  public async freshchat(
    command:
      | 'init'
      | 'config'
      | 'open'
      | 'hide'
      | 'show'
      | 'destroy'
      | 'trackPage',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data?: any
  ): Promise<void> {
    if (!window.fcWidget) {
      await this.checkFreshchatLoaded();
    }

    if (!window.fcWidget) {
      const logger = getLogger();
      logger.error('FreshChat widget is not loaded');
      return;
    }

    try {
      switch (command) {
        case 'init':
          window.fcWidget.init(data);
          this.isInitializedFlag = true;
          break;
        case 'config':
          window.fcWidget.setConfig(data);
          break;
        case 'open':
          window.fcWidget.open();
          break;
        case 'hide':
          window.fcWidget.hide();
          break;
        case 'show':
          window.fcWidget.show();
          break;
        case 'destroy':
          window.fcWidget.destroy();
          this.isInitializedFlag = false;
          break;
        case 'trackPage':
          window.fcWidget.trackPage(data.url);
          break;
      }
    } catch (error) {
      const logger = getLogger();
      logger.error('FreshChat command failed', {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  public async freshchatUser(
    command: 'setProperty' | 'setProperties',
    params: FreshchatUserConfig
  ): Promise<void> {
    if (!window.fcWidget) {
      await this.checkFreshchatLoaded();
    }

    if (!window.fcWidget) {
      const logger = getLogger();
      logger.error('FreshChat widget is not loaded');
      return;
    }

    try {
      if (command === 'setProperty' && window.fcWidget.userProperties) {
        // Set individual property
        if (window.fcWidget.userProperties.set()) {
          // This is a placeholder - actual implementation depends on FreshChat API
        }
      } else if (command === 'setProperties' && window.fcWidget.user) {
        // Set multiple properties at once
        const properties: { [key: string]: string } = {};

        if (params.firstName) properties.firstName = params.firstName;
        if (params.lastName) properties.lastName = params.lastName;
        if (params.email) properties.email = params.email;
        if (params.phone) properties.phone = params.phone;

        window.fcWidget.user.setProperties(properties);
      }
    } catch (error) {
      const logger = getLogger();
      logger.error('FreshChat user command failed', {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  public isLoaded(): boolean {
    return this.isLoadedFlag && window.fcWidget !== undefined;
  }

  public isInitialized(): boolean {
    return (
      this.isInitializedFlag && window.fcWidget?.isInitialized?.() === true
    );
  }

  public async setExternalId(externalId: string): Promise<void> {
    if (!window.fcWidget) {
      await this.checkFreshchatLoaded();
    }

    if (!window.fcWidget) {
      const logger = getLogger();
      logger.error('FreshChat widget is not loaded');
      return;
    }

    try {
      if (window.fcWidget.session && window.fcWidget.session.setExternalId) {
        window.fcWidget.session.setExternalId(externalId);
      }
    } catch (error) {
      const logger = getLogger();
      logger.error('Failed to set external ID', {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }
}
